export type WorkspacePreset = "chart_focus" | "balanced" | "order_focus";
export type OrderCatalogSort =
  | "frequency"
  | "body_region"
  | "alphabetical"
  | "shorthand";

export interface WorkspacePreferencesV1 {
  version: 1;
  preset: WorkspacePreset;
  chartPercent: number;
  showDesktopQueue: boolean;
  orderSort: OrderCatalogSort;
}

const storageKey = "lys-orthoos.workspace-preferences.v1";

export const defaultWorkspacePreferences: WorkspacePreferencesV1 = {
  version: 1,
  preset: "chart_focus",
  chartPercent: 62,
  showDesktopQueue: true,
  orderSort: "frequency",
};

const presets: Readonly<Record<WorkspacePreset, number>> = {
  chart_focus: 68,
  balanced: 58,
  order_focus: 50,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPreset(value: unknown): value is WorkspacePreset {
  return value === "chart_focus" || value === "balanced" || value === "order_focus";
}

function isSort(value: unknown): value is OrderCatalogSort {
  return (
    value === "frequency" ||
    value === "body_region" ||
    value === "alphabetical" ||
    value === "shorthand"
  );
}

export function preferencesForPreset(
  preset: WorkspacePreset,
  current: WorkspacePreferencesV1,
): WorkspacePreferencesV1 {
  return { ...current, preset, chartPercent: presets[preset] };
}

export function loadWorkspacePreferences(): WorkspacePreferencesV1 {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    if (!raw) return defaultWorkspacePreferences;
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || value.version !== 1) return defaultWorkspacePreferences;
    const chartPercent = value.chartPercent;
    if (
      !isPreset(value.preset) ||
      !isSort(value.orderSort) ||
      typeof value.showDesktopQueue !== "boolean" ||
      typeof chartPercent !== "number" ||
      !Number.isFinite(chartPercent)
    ) {
      return defaultWorkspacePreferences;
    }
    return {
      version: 1,
      preset: value.preset,
      chartPercent: Math.min(72, Math.max(48, Math.round(chartPercent))),
      showDesktopQueue: value.showDesktopQueue,
      orderSort: value.orderSort,
    };
  } catch {
    return defaultWorkspacePreferences;
  }
}

export function saveWorkspacePreferences(value: WorkspacePreferencesV1): void {
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(value));
  } catch {
    // The cockpit remains usable when storage is blocked by the browser.
  }
}
